import React, { useState } from 'react';
import styled from '@emotion/styled';
import ReceiptIcon from '~/Assets/icons/receipt.svg';
import ArrowIcon from '~/Assets/icons/arrow_forward.svg';
import DeleteIcon from '~/Assets/icons/delete.svg';
import { useDrop, useDrag, DragSourceMonitor, DropTargetMonitor } from 'react-dnd';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import { isSplitChequeCrashFixCompany, runDndDrop } from '~splitChequeCrashFix';
import {
  useDispatch,
  useI18n,
  useSelector,
  useStaffCan,
  useKitchenMessage,
  useWaste,
} from '~Hooks';

import ItemPreview from '../ItemPreview';
import itemsType from '../dragItemsType';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';

import * as actions from '~actions';
import { useReportsApi } from '~Components/ReportsUniverse';
import { useStorage } from '~Components/Storage';
import { useCompetition, SUBTRACT_SALE_TYPE } from '~Providers/CompetitionsProvider';
import RemoveItems from '~Components/RemoveItems';
import Member from './Member';
import { useInventoryApi, useInventoryUniverse } from '~Components/InventoryUniverse';
import { useOrderStatusApi } from '~Components/OrderStatusUniverse';

const Cheque = styled.div<{
  selected: boolean;
  isDroppable: boolean;
  isOver: boolean;
  showAnimation: boolean;
  staffCanMutate: boolean;
}>`
  background: var(--colorBlueGray200);
  border-radius: 4px;
  border: 1px solid;
  border-color: ${(props) => (props.selected ? 'var(--colorUIYellow)' : 'transparent')};
  box-shadow: ${(props) => (props.selected ? '0px 0px 16px 0px rgba(0, 0, 0, 0.32)' : 'none')};
  height: 128px;
  width: 88px;
  padding: 0 4px;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: ${(props) => (props.selected ? 'var(--colorBlueGray1200)' : 'var(--colorBlueGray900)')};
  font-size: 14px;
  line-height: 16px;
  font-weight: 600;
  letter-spacing: 0.24px;
  text-transform: uppercase;
  margin: 16px 0;
  box-sizing: border-box;
  position: relative;
  z-index: ${(props) => (props.isDroppable ? 3 : 'auto')};
  transition: opacity 0.2s ease-in-out;
  opacity: ${({ staffCanMutate, isDroppable, isOver }) =>
    (staffCanMutate && !isDroppable) || (staffCanMutate && isOver)
      ? '1'
      : isDroppable && staffCanMutate
      ? '0.75'
      : '0.5'};
  animation: ${(props) =>
    props.showAnimation
      ? `
          new-check ${400}ms cubic-bezier(.28,1.81,.53,1.02);
        `
      : ''};

  &:after {
    content: '';
    height: 24px;
    width: 24px;
    mask-image: url(${ArrowIcon});
    mask-position: center;
    mask-size: contain;
    mask-repeat: no-repeat;
    background-color: var(--colorBlueGray200);
    position: absolute;
    top: 52px;
    right: -28px;
    opacity: ${(props) => (props.selected ? 1 : 0)};
    transition: opacity 0.2s ease-in-out;
  }
`;

const ChequeIcon = styled.div`
  height: 24px;
  width: 24px;
  mask-image: url(${ReceiptIcon});
  mask-position: center;
  mask-size: contain;
  mask-repeat: no-repeat;
  margin-bottom: 4px;
  background-color: var(--colorBlueGray800);
`;

const DeleteIconWrapper = styled.div`
  height: 24px;
  width: 24px;
  mask-image: url(${DeleteIcon});
  mask-position: center;
  mask-size: contain;
  mask-repeat: no-repeat;
  margin-bottom: 4px;
  position: absolute;
  bottom: 0;
  right: -30px;
  background-color: var(--colorBlueGray200);
`;

const Name = styled.span`
  word-break: break-word;
`;

const OverLay = styled.div<{
  show: boolean;
}>`
  position: fixed;
  width: 100%;
  height: 100%;
  background-color: rgb(0 0 0 / 50%);
  left: 0;
  top: 0;
  z-index: 2;
  pointer-events: none;
  opacity: ${(props) => (props.show ? 1 : 0)};
  transition: opacity 0.2s ease-in-out;
`;

const STATUS_COLORS = {
  NONE: 'transparent',
  PENDING: 'rgb(200 192 50 / 42%)',
  COMPLETED: 'rgba(50, 200, 50, 0.6)',
  FAILED: 'rgba(200, 50, 50, 0.5)',
};

const PaymentOverlay = styled.div<{
  status: 'NONE' | 'PENDING' | 'COMPLETED' | 'FAILED';
}>`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: ${(props) => STATUS_COLORS[props.status]};
  left: 0;
  top: 0;
  z-index: 2;
  pointer-events: none;
`;

const Owner = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 8px;
  font-size: 8px;
  line-height: 1.2;
  word-break: break-word;
  padding: 0 2px;
`;

interface Props {
  chequeId: string;
  chequeName: string;
  ownerId: string;
  selected: boolean;
  setSelectedCheque: (val: string) => void;
  showNewCheckAnimation: boolean;
  showLogin: () => void;
  table: {
    id: string;
    name: string;
  } | null;
}

const ChequeItem: React.FunctionComponent<Props> = ({
  chequeId,
  chequeName,
  ownerId,
  table,
  selected,
  setSelectedCheque,
  showNewCheckAnimation,
  showLogin,
}) => {
  const dispatch = useDispatch();
  const { i18n } = useI18n();
  const companyStaff = useSelector((state) => state.companyStaff?.staffs);
  const reportsApi = useReportsApi();
  const staffId = useSelector((state) =>
    state.companyStaff?.staffs.length ? state.staff?.id : false,
  );
  const venueId = useSelector((state) => state.venueId);
  const messageModal = useModal();
  const api = useTableApi();
  const universe = useTableUniverse();
  // only defer the drop mutation for the affected company
  const useFix = isSplitChequeCrashFixCompany(useSelector((state) => state.company));
  const waste = useWaste();
  const [storage] = useStorage();
  const competitions = useCompetition();
  const sendKitchenMessage = useKitchenMessage();

  const inventoryUniverse = useInventoryUniverse();
  const inventoryApi = useInventoryApi(venueId);
  const orderStatusApi = useOrderStatusApi(venueId || '');

  const chequeItems = universe.items.filter((c) => c.chequeId === chequeId);

  const cheque = universe.cheques.find((c) => c.id === chequeId)!;

  const ownerName = companyStaff?.find((s) => s.id === ownerId)?.name;
  const staffCan = useStaffCan();
  // Currently we use REFUND permission
  const staffCanDelete = staffCan.REFUND;
  const staffCanMutate = staffCan.mutateCheque(chequeId);

  const order = useSelector((state) =>
    state.POSOrders.find((order) => order.chequeId === chequeId),
  );

  const payment = useSelector((state) =>
    state.POSPayments.find((payment) => payment.order === order?.id),
  );

  const [{ isDragging }, drag] = useDrag({
    item: { type: itemsType.CHEQUE, chequeId, table },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: staffCanMutate,
  });

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [itemsType.CHEQUE, itemsType.ITEM],
    canDrop: (item: any) => {
      return staffCanMutate && item.chequeId !== chequeId;
    },

    drop: (data) => {
      // Defer the mutation until the dnd drop transaction has completed.
      // Mutating synchronously re-renders and unregisters drop targets
      // mid-transaction, crashing react-dnd with "Expected to find a valid target."
      runDndDrop(useFix, () => {
        switch (data.type) {
          case itemsType.ITEM:
            api.moveManyItemsToCheque(data.selectedItems, chequeId);
            break;
          case itemsType.CHEQUE:
            api.mergeCheque(data.chequeId, chequeId);
            api.log.mergeCheque(data.chequeId, chequeId);
            setSelectedCheque(chequeId);
            break;
          default:
            break;
        }
      });
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const removeCheque = (message: string) => {
    api.removeCheque(chequeId, cheque?.tableId || '');
    api.log.removeCheque(chequeId, message);
  };

  const removeChequeItems = (message: string, commentOnWaste?: string) => {
    const itemsOnChequeForDeletion = chequeItems;
    const totalAmount = chequeItems.reduce((acc, item) => {
      return (acc += item.amount - item.discount);
    }, 0);

    api.removeItems(
      chequeItems,
      chequeId,
      message,
      cheque?.tableId || '',
      Boolean(commentOnWaste),
    );

    // Mark the removed items as cancelled on the KDS. Items that were never
    // sent to a KDS station don't exist in the order-status universe, so this
    // is a no-op for them.
    chequeItems.forEach((item) => {
      orderStatusApi.cancelItem(item.id, message);
    });

    if (commentOnWaste !== undefined) {
      const itemsToWaste = itemsOnChequeForDeletion.map((i) => ({
        itemId: i.itemId,
        fraction: i.fraction,
        title: i.title,
        message: i.message,
      }));
      waste.register(itemsToWaste, message, commentOnWaste);
    } else {
      /** Correction - add back inventory */
      itemsOnChequeForDeletion.forEach((item) => {
        const invItem = inventoryUniverse.items.find((i) => i.itemId === item.itemId);

        if (invItem && typeof invItem.count === 'number') {
          inventoryApi.addStock(invItem.itemId, item.fraction);
        }
      });
    }

    // Only triggers remove cheque line correction when there are items on a cheque
    reportsApi.orderLineCorrected('REMOVE_CHEQUE', totalAmount);

    // We subtract from the owner of the cheque, because a common scenario IRL is that the head waiter removes items from a cheque
    competitions.subtractSale({
      staffId: cheque?.owner,
      items: itemsOnChequeForDeletion,
      type: SUBTRACT_SALE_TYPE.REMOVE_CHEQUE,
    });

    if (storage.printVoidedItems) {
      const voidItems = itemsOnChequeForDeletion.map((item) => {
        return {
          itemId: item.itemId,
          categoryId: item.categoryId,
          title: item.title,
          fraction: item.fraction,
          price: item.amount - item.discount,
        };
      });

      sendKitchenMessage({
        type: 'VOID_CHEQUE',
        tableName: table?.name ?? '',
        staffName: companyStaff?.find((s) => s.id === staffId)?.name ?? '',
        items: voidItems,
        message,
      });
    }

    removeCheque(message);
  };

  const handleDeleteClick = () => {
    if (staffId === null) {
      showLogin();
      return;
    }

    // If there's payments on the cheque, you're not allowed to delete it
    if (cheque.paidAmount > 0) {
      dispatch(actions.addToast(i18n('TM.CantRemoveChequeWithPayments'), true, null, true));
      return;
    }

    if (chequeItems.length > 0) {
      messageModal.toggle();
    } else {
      removeCheque('--');
    }
  };

  const renderCheque = (preview?: boolean) => {
    return (
      <Cheque
        isDroppable={canDrop}
        isOver={isOver}
        ref={(node) => (staffId !== null ? drag(drop(node)) : null)}
        selected={preview ?? selected}
        onTouchStart={() => {
          setSelectedCheque(chequeId);
        }}
        showAnimation={showNewCheckAnimation}
        staffCanMutate={staffCanMutate}>
        {cheque.userId && <Member />}
        <ChequeIcon />
        <Name>{chequeName.substring(0, 18)}</Name>
        {order && <PaymentOverlay status={payment ? payment.status : 'NONE'} />}
        {selected && staffCanDelete && <DeleteIconWrapper onClick={handleDeleteClick} />}
        {ownerName && <Owner>{ownerName}</Owner>}
      </Cheque>
    );
  };

  return (
    <>
      <OverLay show={isDragging} />
      <ItemPreview
        type={itemsType.CHEQUE}
        chosenItemId={chequeId}
        renderItem={renderCheque(false)}
        styles={{
          top: -80,
          left: -45,
        }}
      />
      {renderCheque()}

      <Modal isShowing={messageModal.isShowing} hide={messageModal.toggle} position="flex-start">
        <RemoveItems
          onComplete={removeChequeItems}
          chequeId={chequeId}
          itemIdsToRemove={chequeItems.map((i) => i.id)}
        />
      </Modal>
    </>
  );
};

export default React.memo(ChequeItem);
